// Frameworks & Libraries
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { useMemo, useRef, useEffect, useState } from 'react'

// MUI
import { Alert, Box, LinearProgress } from '@mui/material'

// Store & API
import { TimesheetStoreState } from '../../store/timesheetStore'

// Components
import Loader from '../general/Loader'
import ActionButtons from '../ActionButtons'
import ApprovalDetails from './ApprovalDetails'
import SubHeaderDetails from './SubHeaderDetails'
import HeaderApprovalDetails from './HeaderApprovalDetails'

// Hooks
import { useGetTimesheetQuery } from '../../utils/hooks/useGetTimesheetQuery'

function ApprovalDetailsPage(): React.ReactElement {
	// #region React Router hooks

	const location = useLocation()
	const isPrintRoute = location.pathname.endsWith('/print') // Detect if it's on the print route

	// #endregion

	// #region Redux state and API
	const timesheetPageDetail = useSelector((state: TimesheetStoreState) => state.timesheetPageDetail)
	const timesheetLoading = useSelector((state: TimesheetStoreState) => state.timesheetLoading)
	const timesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)

	const { isFetching: isFetchingTimesheet } = useGetTimesheetQuery()

	// #endregion

	// #region Refs
	const approvalDetailsRef = useRef<HTMLDivElement>(null)
	const pdfContentRef = useRef<HTMLDivElement>(null)

	// #endregion

	// #region Local state

	const [approvalDetailsHeight, setApprovalDetailsHeight] = useState<number>(0)
	const [approvalDetailsTop, setApprovalDetailsTop] = useState<number>(0)

	// #endregion

	// #region Effects

	/**
	 * Effect to measure the height and top position of the approval details section relative to the PDF content container.
	 * It uses a ResizeObserver to update these measurements whenever the size of the approval details or the container changes, ensuring that the loading overlay can be accurately positioned during timesheet fetching.
	 * The effect depends on the driver's name and the fetching state of the timesheet to trigger re-measurement when relevant data changes.
	 * @see {@link ResizeObserver} for observing changes to the size of the approval details and container elements.
	 * @see {@link useGetTimesheetQuery} for fetching timesheet data and triggering loading states.
	 * @param timesheetPageDetail.driver.driverName - The name of the driver, used as a dependency to trigger re-measurement when the timesheet details change.
	 * @param isFetchingTimesheet - The fetching state of the timesheet, used to trigger re-measurement when the loading state changes.
	 */
	useEffect(() => {
		const element = approvalDetailsRef.current
		const container = pdfContentRef.current
		if (!element || !container) return

		const updateDimensions = () => {
			const elementRect = element.getBoundingClientRect()
			const containerRect = container.getBoundingClientRect()
			const height = elementRect.height
			const top = elementRect.top - containerRect.top
			setApprovalDetailsHeight(height)
			setApprovalDetailsTop(top)
		}

		// Measure initial dimensions
		updateDimensions()

		// Use ResizeObserver to detect changes
		const resizeObserver = new ResizeObserver(() => {
			updateDimensions()
		})

		resizeObserver.observe(element)
		resizeObserver.observe(container)

		return () => {
			resizeObserver.disconnect()
		}
	}, [timesheetPageDetail.driver.driverName, isFetchingTimesheet])

	// #endregion

	// Memoize content to prevent unnecessary re-renders and calculations
	const content = useMemo(() => {
		if (timesheetLoading.loading && timesheetPageDetail.driver.driverName === '') {
			return <Loader />
		}

		if (timesheetPageDetail.driver.driverName === '') {
			return (
				<Box
					component={'div'}
					sx={{ width: '100%', display: 'flex' }}
				>
					<Alert
						severity='info'
						sx={{
							width: '100%',
							height: '37px',
							padding: '0 16px',
							background: 'linear-gradient(90deg, #00A5FF 0%, #37CDFF 100%)',
							color: 'white',
							fontFamily: 'var(--font-family)',
							fontSize: '14px',
							fontWeight: 500,
							letterSpacing: '0.15px',
							lineHeight: '150%',
							'& .MuiAlert-icon': {
								color: 'white',
							},
						}}
					>
						Selecione uma Folha de Ponto para gestão
					</Alert>
				</Box>
			)
		}

		return (
			<Box
				sx={{
					position: 'relative',
					padding: 0,
					height: 'calc(100dvh - 10.75rem)',
					overflowY: 'visible',
				}}
			>
				{isFetchingTimesheet && (
					<Box sx={{ width: '100%' }}>
						<LinearProgress color='primary' />
					</Box>
				)}
				<Box
					sx={{
						width: '100%',
						height: '100%',
						minHeight: '400px',
						overflowY: 'visible',
						position: 'relative',
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					<HeaderApprovalDetails />
					<Box
						id='pdf-content'
						sx={{
							display: 'grid',
							gap: 2,
							overflowY: 'auto',
							paddingBlockEnd: 1,
						}}
					>
						<SubHeaderDetails />
						<ApprovalDetails key={timesheetPage.rowVersion} />
					</Box>

					{!isPrintRoute && <ActionButtons />}
					{isFetchingTimesheet && (
						<Box
							sx={{
								position: 'absolute',
								top: approvalDetailsTop > 0 ? `${approvalDetailsTop}px` : 0,
								left: 0,
								right: 0,
								width: '100%',
								height: approvalDetailsHeight > 0 ? `${approvalDetailsHeight}px` : '100%',
								zIndex: 10000,
								bgcolor: 'white',
								opacity: 0.5,
								pointerEvents: 'auto',
								cursor: 'not-allowed',
							}}
						/>
					)}
				</Box>
			</Box>
		)
	}, [
		timesheetLoading.loading,
		timesheetPageDetail.driver.driverName,
		approvalDetailsHeight,
		approvalDetailsTop,
		isFetchingTimesheet,
	])

	return content
}

export default ApprovalDetailsPage
